/**
 * Alfabeto del juego y normalización de letras.
 *
 * El panel se juega en español, y eso trae tres decisiones que afectan a todo lo
 * demás y conviene tener en un solo sitio:
 *
 * - La **Ñ** es una letra propia: quien pide la N no descubre las Ñ.
 * - Las **vocales acentuadas** cuentan como su vocal sin tilde (pedir la A
 *   descubre también las Á), porque en el concurso el acento no es información
 *   que haya que adivinar. La Ü cuenta como U por el mismo motivo.
 * - Los **dígrafos** CH y LL no son letras: son C+H y L+L.
 */

/** Vocales del juego, en el orden en que se ofrecen. */
export const VOWELS = ['A', 'E', 'I', 'O', 'U'] as const;

/** Consonantes del juego, en orden alfabético español (la Ñ va tras la N). */
export const CONSONANTS = [
  'B', 'C', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M',
  'N', 'Ñ', 'P', 'Q', 'R', 'S', 'T', 'V', 'W', 'X',
  'Y', 'Z',
] as const;

/** Una letra del juego: vocal o consonante, siempre en mayúscula. */
export type Letter = (typeof VOWELS)[number] | (typeof CONSONANTS)[number];

/** Todas las letras, para recorrer el abecedario de una vez. */
export const ALPHABET: readonly Letter[] = [...VOWELS, ...CONSONANTS].sort((a, b) =>
  a.localeCompare(b, 'es'),
);

const VOWEL_SET = new Set<string>(VOWELS);
const CONSONANT_SET = new Set<string>(CONSONANTS);

/**
 * @brief Reduce un carácter a la letra del juego que le corresponde.
 *
 * Pasa a mayúscula y quita la tilde y la diéresis, conservando la Ñ. Lo que no
 * sea una letra del alfabeto (espacios, signos, cifras) devuelve null.
 *
 * @param character Carácter suelto tal y como viene en la frase o del jugador.
 * @return La letra del juego, o null si ese carácter no es una letra.
 */
export function toLetter(character: string): Letter | null {
  const upper = character.toUpperCase();
  if (upper === 'Ñ') return 'Ñ';
  // La descomposición Unicode separa la vocal de su tilde; borrando las marcas
  // diacríticas quedan Á→A y Ü→U sin tener que enumerar los casos a mano. La Ñ
  // se ha resuelto antes justo porque este paso también la desharía en N.
  const plain = upper.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  if (plain.length !== 1) return null;
  if (VOWEL_SET.has(plain) || CONSONANT_SET.has(plain)) return plain as Letter;
  return null;
}

/** @brief true si la letra es una vocal del juego. */
export function isVowel(letter: Letter): boolean {
  return VOWEL_SET.has(letter);
}

/** @brief true si la letra es una consonante del juego. */
export function isConsonant(letter: Letter): boolean {
  return CONSONANT_SET.has(letter);
}

/**
 * @brief Interpreta lo que ha escrito el jugador como una letra del juego.
 *
 * Tolera espacios alrededor y minúsculas, pero exige **una sola** letra: si
 * escribe una palabra entera no se adivina cuál quería, se rechaza y se le
 * vuelve a preguntar.
 *
 * @param input Texto tal cual lo ha escrito el jugador.
 * @return La letra, o null si lo escrito no es una letra suelta.
 */
export function parseLetter(input: string): Letter | null {
  const clean = input.trim();
  if ([...clean].length !== 1) return null;
  return toLetter(clean);
}
